<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

const props = defineProps<{
  data: SectionServicesListRecord
  moduleIndex: number
}>()

const { level, isSemantic, invertedStyles } = useSectionRules(
  props.moduleIndex,
  props.data.invertColorTheme,
  !!props.data.heading
)

const ctxRef = ref<HTMLElement | null>(null)
const servicesRef = ref<HTMLElement | null>(null)
const { height } = useElementSize(servicesRef)

let ctx: gsap.Context | null = null

onMounted(() => {
  if (ctxRef.value) {
    gsap.registerPlugin(ScrollTrigger)
    const mm = gsap.matchMedia()

    mm.add('(min-width: 1024px)', () => {
      ctx = gsap.context((self: gsap.Context) => {
        const elPin = self.selector ? self.selector('.fx-services-title') : null
        ScrollTrigger.create({
          trigger: elPin,
          pin: elPin,
          start: 'top center',
          end: `+=${height.value}`,
          markers: false
        })
        ScrollTrigger.refresh()
      }, ctxRef.value as HTMLElement)
    })
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div
    ref="ctxRef"
    class="container grid-default overflow-hidden pb-16 pt-28 lg:gap-y-40"
    :class="[
      {
        'lg:py-[50vh]': data.layout === 'Large',
        'lg:py-50': data.layout === 'Medium'
      },
      invertedStyles
    ]"
  >
    <template v-if="data.layout === 'Large'">
      <div class="col-span-full xl:col-span-4">
        <div class="fx-services-title transform-gpu">
          <Heading
            v-if="data.heading"
            :level="level"
            :size="1"
            :is-semantic="isSemantic"
            class="col-span-full mb-7 flex text-right uppercase xl:justify-end"
          >
            <TextReveal>{{ data.heading }}</TextReveal>
          </Heading>
          <div v-if="data.callToAction.length" class="mt-4 hidden xl:block">
            <CallToAction :data="data.callToAction" />
          </div>
        </div>
      </div>
      <ServicesFloatLarge ref="servicesRef" :services="data.services" :layout="data.layout" />
      <div v-if="data.callToAction.length" class="mt-4 xl:hidden">
        <CallToAction :data="data.callToAction" />
      </div>
    </template>
    <template v-else-if="data.layout === 'Medium'">
      <Heading
        v-if="data.heading"
        :level="level"
        :size="1"
        :is-semantic="isSemantic"
        class="col-span-full mb-7"
      >
        <TextReveal>{{ data.heading }}</TextReveal>
      </Heading>
      <ServicesFloatMedium :services="data.services" :layout="data.layout" />
      <div v-if="data.callToAction.length" class="mt-4">
        <CallToAction :data="data.callToAction" />
      </div>
    </template>
  </div>
</template>
